import { useEffect, useRef, useState } from 'react';
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Search as SearchIcon,
} from 'lucide-react';
import { useStore, activeSheet } from '../store';
import type { ReqCategory, ReqPriority, ReqStatus } from '../types';

function isInteractiveTarget(target: EventTarget | null) {
  const el = target instanceof HTMLElement ? target : null;
  return Boolean(el?.closest('input, textarea, button, select'));
}

const CATEGORIES: ReqCategory[] = [
  'Functional',
  'Performance',
  'Safety',
  'Interface',
  'Environmental',
  'Mechanical',
  'Other',
];

const PRIORITIES: ReqPriority[] = ['Must', 'Should', 'Could', 'Won\u2019t'];
const STATUSES: ReqStatus[] = ['Draft', 'Approved', 'Implemented', 'Verified', 'Rejected'];

const PRIORITY_COLOR: Record<ReqPriority, string> = {
  Must: '#dc2626',
  Should: '#f59e0b',
  Could: '#2563eb',
  'Won\u2019t': '#64748b',
};

const STATUS_COLOR: Record<ReqStatus, string> = {
  Draft: '#64748b',
  Approved: '#2563eb',
  Implemented: '#8b5cf6',
  Verified: '#16a34a',
  Rejected: '#dc2626',
};

export function RequirementsSheet() {
  const sheet = useStore(activeSheet);
  const addRequirement = useStore((s) => s.addRequirement);
  const updateRequirement = useStore((s) => s.updateRequirement);
  const removeRequirement = useStore((s) => s.removeRequirement);
  const moveRequirement = useStore((s) => s.moveRequirement);

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<ReqCategory | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<ReqStatus | 'all'>('all');
  // Accordion — collapsed by default so a long requirements list stays
  // scannable; newly added requirements auto-expand for immediate editing.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const all = sheet.requirements ?? [];
  const knownIds = useRef<Set<string>>(new Set(all.map((r) => r.id)));
  useEffect(() => {
    const newIds = all.filter((r) => !knownIds.current.has(r.id)).map((r) => r.id);
    knownIds.current = new Set(all.map((r) => r.id));
    if (newIds.length > 0) setExpanded((prev) => new Set([...prev, ...newIds]));
  }, [all]);

  const q = search.trim().toLowerCase();
  const filtered = all.filter((r) => {
    if (filterCategory !== 'all' && r.category !== filterCategory) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (!q) return true;
    const blob = `${r.reqNumber} ${r.title} ${r.description} ${r.verification ?? ''} ${r.notes ?? ''}`.toLowerCase();
    return blob.includes(q);
  });

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-canvas)',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          background: 'var(--panel)',
          borderBottom: '1px solid var(--panel-border)',
        }}
      >
        <button
          onClick={() => addRequirement()}
          style={primaryBtn}
          title="Add requirement"
        >
          <Plus size={14} /> Add requirement
        </button>
        <div
          style={{
            position: 'relative',
            flex: 1,
            maxWidth: 360,
          }}
        >
          <SearchIcon
            size={13}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              pointerEvents: 'none',
            }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search requirements…"
            style={{
              width: '100%',
              border: '1px solid var(--panel-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '6px 10px 6px 28px',
              fontSize: 12,
              background: 'var(--panel)',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value as ReqCategory | 'all')}
          style={selectStyle}
        >
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as ReqStatus | 'all')}
          style={selectStyle}
        >
          <option value="all">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {filtered.length} of {all.length}
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {all.length === 0 && (
          <div style={emptyStyle}>
            <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
              No requirements yet
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Capture functional, performance, safety, and interface needs here.
              <br />
              Each requirement is included in the Spec .xlsx export.
            </div>
            <button onClick={() => addRequirement()} style={primaryBtn}>
              <Plus size={14} /> Add your first requirement
            </button>
          </div>
        )}
        {all.length > 0 && filtered.length === 0 && (
          <div style={emptyStyle}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              No requirements match your filters.
            </div>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((r, idx) => {
            const isOpen = expanded.has(r.id);
            return (
            <div key={r.id} style={rowStyle}>
              <div
                style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}
                onClick={(e) => {
                  if (isInteractiveTarget(e.target)) return;
                  toggleExpanded(r.id);
                }}
              >
                {isOpen ? (
                  <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                ) : (
                  <ChevronRight size={14} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                )}
                <input
                  value={r.reqNumber}
                  onChange={(e) =>
                    updateRequirement(r.id, { reqNumber: e.target.value })
                  }
                  style={{ ...input, width: 72, fontWeight: 700 }}
                />
                <select
                  value={r.category}
                  onChange={(e) =>
                    updateRequirement(r.id, { category: e.target.value as ReqCategory })
                  }
                  style={{ ...input, width: 130 }}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <input
                  value={r.title}
                  onChange={(e) => updateRequirement(r.id, { title: e.target.value })}
                  placeholder="Title — one-line summary"
                  style={{ ...input, flex: 1, fontWeight: 600 }}
                />
                <select
                  value={r.priority}
                  onChange={(e) =>
                    updateRequirement(r.id, { priority: e.target.value as ReqPriority })
                  }
                  style={{
                    ...input,
                    width: 84,
                    color: PRIORITY_COLOR[r.priority],
                    fontWeight: 600,
                  }}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <select
                  value={r.status}
                  onChange={(e) =>
                    updateRequirement(r.id, { status: e.target.value as ReqStatus })
                  }
                  style={{
                    ...input,
                    width: 112,
                    color: STATUS_COLOR[r.status],
                    fontWeight: 600,
                  }}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => moveRequirement(r.id, -1)}
                  disabled={idx === 0}
                  style={iconGhost}
                  title="Move up"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  onClick={() => moveRequirement(r.id, 1)}
                  disabled={idx === filtered.length - 1}
                  style={iconGhost}
                  title="Move down"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Delete requirement ${r.reqNumber}?`)) {
                      removeRequirement(r.id);
                    }
                  }}
                  style={{ ...iconGhost, color: 'var(--danger)' }}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {!isOpen && r.description && (
                <div style={collapsedPreviewStyle} title={r.description}>
                  {r.description}
                </div>
              )}
              {isOpen && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
                  <div>
                    <div style={labelStyle}>Description</div>
                    <textarea
                      rows={3}
                      value={r.description}
                      onChange={(e) =>
                        updateRequirement(r.id, { description: e.target.value })
                      }
                      placeholder="What is required, in detail. Why it matters."
                      style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }}
                    />
                  </div>
                  <div>
                    <div style={labelStyle}>Verification</div>
                    <textarea
                      rows={3}
                      value={r.verification ?? ''}
                      onChange={(e) =>
                        updateRequirement(r.id, { verification: e.target.value })
                      }
                      placeholder="How to verify this requirement (inspection, test, analysis)."
                      style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }}
                    />
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(37, 99, 235, 0.25)',
};

const selectStyle: React.CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '5px 8px',
  fontSize: 12,
  background: 'var(--panel)',
  color: 'var(--text)',
  cursor: 'pointer',
};

const input: React.CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 5,
  padding: '5px 8px',
  fontSize: 12,
  outline: 'none',
  boxSizing: 'border-box',
  width: '100%',
};

const iconGhost: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1px solid var(--panel-border)',
  borderRadius: 5,
  cursor: 'pointer',
  flexShrink: 0,
};

const rowStyle: React.CSSProperties = {
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
  padding: 12,
  boxShadow: 'var(--shadow-sm)',
};

const collapsedPreviewStyle: React.CSSProperties = {
  marginTop: 6,
  paddingLeft: 22,
  fontSize: 11.5,
  color: 'var(--text-muted)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  marginBottom: 3,
  fontWeight: 500,
};

const emptyStyle: React.CSSProperties = {
  padding: 28,
  textAlign: 'center',
  border: '1px dashed var(--panel-border-strong)',
  borderRadius: 'var(--radius)',
  background: 'var(--panel)',
  margin: '20px auto',
  maxWidth: 460,
};
